<template>
  <div
    v-if="languages.length > 1"
    :id="id ? id + '-lang-selector' : null"
    class="cat-language-selector"
  >
    <template v-if="languages.length === 2">
      <template v-for="(local, idx) in languages">
        <cat-btn
          v-if="local.value !== selectedLocale"
          :key="idx"
          small
          link
          simple
          :hint="offscreenLabel"
          :show-hint="false"
          :lang="local.value"
          @ui-click="_onChange({ value: local.value })"
          ><cat-icon icon="globe" class="cat-language-icon" />{{
            local.text
          }}</cat-btn
        >
      </template>
    </template>

    <inline-menu
      v-else
      :items="languages"
      right
      :value="selectedLocale"
      :label="selectedLocale.toUpperCase()"
      class="language-selector-menu"
      :offscreen-label="offscreenLabel"
      @ui-select="_onChange"
    >
      <template #item="{ text, value: itemValue }">
        <span class="item" :lang="itemValue">
          <span class="lang-text">{{ text }} </span>
          <abbr class="lang-code" :title="text">{{
            itemValue.toUpperCase()
          }}</abbr>
        </span>
      </template>

      <template #trigger>
        <span>
          <cat-icon icon="globe" class="cat-language-icon" />
          <span :lang="selectedLocale">{{ selectedText }}</span>
        </span>
      </template>
    </inline-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatBtn from "@/components/catBtn/catBtn.vue";
import CatIcon from "@/components/utilities/catIcon.vue";
import InlineMenu from "@/components/catMenu/catMenu.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatLanguageSelector",
  components: { CatBtn, CatIcon, InlineMenu },
  props: {
    id: [Number, String],
    value: {
      type: String,
      description: "The currently selected language."
    },
    locales: {
      type: Array,
      description:
        "List of supported locales and needs to be available from supportedLocales."
    },
    supportedLocales: {
      type: Array,
      description: "List of locale details.",
      default: () => []
    },
    delegateChangeEvent: {
      type: Boolean,
      default: false,
      description:
        "If set to true, the change language event is emitted to the caller instead for redirecting the page with the language in the request parameter."
    }
  },
  computed: {
    offscreenLabel: function () {
      return this.resolveTxt(
        "skin.lang.selector.label",
        "Set site language. Your current language is " + this.selectedText
      );
    },
    selectedText: function () {
      let selectedIndex = this.supportedLocales.findIndex(
        (n) => n.value === this.selectedLocale
      );

      return this.supportedLocales[selectedIndex].text;
    },
    selectedLocale: function () {
      return this.locales.includes(this.value) ? this.value : this.locales[0];
    },
    languages: function () {
      let newLocales = [];

      for (const i of this.locales) {
        const index = this.supportedLocales.findIndex((n) => n.value === i);

        if (this.supportedLocales[index] !== undefined) {
          newLocales.push(this.supportedLocales[index]);
        }
      }

      return newLocales;
    }
  },
  methods: {
    _onChange: function (payload) {
      if (this.delegateChangeEvent) {
        this.$emit("ui-change", { value: payload.value });
        return;
      }

      var href = new URL(window.location.href);
      href.searchParams.set("lang", payload.value);
      window.location.replace(href);
    }
  }
});
</script>
